import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Printer, X, Loader2, ExternalLink, FileText } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";

export default function PDFPreviewModal({ 
  open, 
  onClose, 
  title,
  documentType, // 'invoice' or 'quote'
  companyId,
  documentId,
  documentNumber,
  onStatusChange // Optional callback when status changes (e.g., mark as sent)
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  useEffect(() => {
    if (open && companyId && documentId) {
      loadPDF();
    }
    
    return () => {
      // Cleanup blob URL when modal closes
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
    };
  }, [open, companyId, documentId]);

  const loadPDF = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("auth_token");
      const endpoint = documentType === 'invoice' 
        ? `/api/companies/${companyId}/invoices/${documentId}/pdf`
        : `/api/companies/${companyId}/quotes/${documentId}/pdf`;
      
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load PDF: ${response.status}`);
      }
      
      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error("Empty PDF received");
      }
      
      // Ensure blob has correct MIME type for PDF viewing in iframe
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      setPdfBlobUrl(url);
    } catch (err) {
      console.error("PDF load error:", err);
      setError(err.message);
      toast.error("Failed to load PDF preview");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!pdfBlobUrl) return;
    
    try {
      const a = document.createElement("a");
      a.href = pdfBlobUrl;
      a.download = `${documentNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download PDF");
    }
  };

  const handlePrint = () => {
    if (!pdfBlobUrl) return;
    
    try {
      // Open PDF in new window for printing
      const printWindow = window.open(pdfBlobUrl, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      } else {
        toast.error("Please allow popups to print");
      }
    } catch (err) {
      console.error("Print error:", err);
      toast.error("Failed to print PDF");
    }
  };

  const handleOpenInNewTab = () => {
    if (!pdfBlobUrl) return;
    window.open(pdfBlobUrl, '_blank');
  };

  const handleClose = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border max-w-5xl h-[90vh] flex flex-col p-0" data-testid="pdf-preview-modal">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {title || `${documentType === 'invoice' ? 'Invoice' : 'Quote'} Preview`}
              {documentNumber && (
                <span className="text-muted-foreground font-mono text-sm ml-2">
                  #{documentNumber}
                </span>
              )}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleOpenInNewTab}
                disabled={loading || error}
                className="border-border text-muted-foreground hover:text-white"
                data-testid="pdf-open-new-tab"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Open
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handlePrint}
                disabled={loading || error}
                className="border-border text-muted-foreground hover:text-white"
                data-testid="pdf-print-btn"
              >
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
              <Button 
                size="sm" 
                onClick={handleDownload}
                disabled={loading || error}
                className="btn-primary-gradient"
                data-testid="pdf-download-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        {/* PDF Viewer */}
        <div className="flex-1 bg-secondary/30 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <Loader2 className="w-10 h-10 text-primary animate-spin" />
              <p className="text-muted-foreground">Loading preview...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <FileText className="w-16 h-16 text-muted-foreground/50" />
              <p className="text-destructive">{error}</p>
              <Button variant="outline" onClick={loadPDF} className="border-border text-white">
                Retry
              </Button>
            </div>
          ) : pdfBlobUrl ? (
            <object
              data={`${pdfBlobUrl}#view=FitH`}
              type="application/pdf"
              className="w-full h-full"
              data-testid="pdf-iframe"
            >
              {/* Fallback for browsers that don't support object tag for PDFs */}
              <iframe
                src={`${pdfBlobUrl}#view=FitH`}
                className="w-full h-full border-0"
                title={`${documentType} Preview`}
              />
            </object>
          ) : null}
        </div>

        {/* Footer with actions */}
        <div className="px-6 py-4 border-t border-border flex-shrink-0 flex items-center justify-between bg-card">
          <p className="text-xs text-muted-foreground">
            Preview generated at {new Date().toLocaleTimeString()}
          </p>
          <Button variant="outline" onClick={handleClose} className="border-border text-white">
            Close Preview
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
